import React, { useMemo, useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
import { ReportCardContainer } from './report-card-container';
import { Typography, Box } from '@material-ui/core';
import { SessionReporter } from '../../../lib/utils/session.reporter';
import { useRejectedAccessLink } from '../../../lib/hooks/use-rejected-access-link';
import { AccessLinkRejected } from '../../_core/AccessLinkRejected/access-link-rejected.component';
import { ReportCardUnavailable } from '../../ReportCardAccess/report-card-unavailable.component';
import { readReportCardUnavailable } from '../../../lib/helpers/graphql-error.helpers';
import { useLogQueryError } from '../../../lib/hooks/use-log-query-error';

const GET_REPORT_CARD = gql`
  query GetReportCard($code: String!, $playerAssessmentId: ID!, $token: String) {
    player(code: $code, token: $token) {
      firstName
      lastName
    }
    assessmentReportCards(code: $code, _id: $playerAssessmentId, token: $token) {
      _id
      assessment {
        _id
        useMedian
        name
        company {
          _id
          name
        }
      }
    }
    playerAssessment(code: $code, _id: $playerAssessmentId, token: $token) {
      _id
      paymentRef
      assessment {
        _id
        name
        company {
          _id
          name
          logo
        }
      }
      position {
        _id
        name
      }
      contacts {
        email
      }
      ageGroup {
        _id
        name
        showReportPayment
        reportPrice
        skillSummaries {
          mean
          median
          best
          skillId
        }
      }
      adjustedScore
      skillSummaries {
        mean
        median
        best
        skillId
      }
      categorySummaries {
        mean
        median
        best
        weightCategoryId
      }
      bestSkillSummaries {
        mean
        median
        best
        skillId
      }
      reportCardLevel
    }
  }
`;

const GET_REPORT_CATEGORIES = gql`
    query GetReportCategories($ageGroupId: ID!, $positionId: ID!) {
      reportWeightCategories(ageGroupId: $ageGroupId, positionId: $positionId) {
        _id
        name
        skills {
          _id
          name
        }
      }
    }
`;

interface IReportCardGraphQL {
  code: string;
  playerAssessmentId: string;
  token?: string;
}

export const ReportCardGraphQL: React.FC<IReportCardGraphQL> = ({ code, playerAssessmentId, token }) => {
  const { data, loading, error } = useQuery(GET_REPORT_CARD, {
    variables: { code, playerAssessmentId, token },
    fetchPolicy: 'network-only'
  });
  useLogQueryError(error, 'GetReportCard');
  const { rejectedLink, signInPath, redirecting } = useRejectedAccessLink({ token, error, loading });

  const playerAssessment = data && data.playerAssessment;
  const assessmentReportCards = (data && data.assessmentReportCards) || [];
  const player = data && data.player;

  // A link viewer has no account, so the report card identifies them; the link token is never sent.
  useEffect(() => {
    if (token && player && !loading && !error) {
      const playerName = `${player.firstName} ${player.lastName}`;

      SessionReporter.identify({
        _id: `report-card-viewer_${playerAssessmentId}`,
        name: `Report Card Viewer - ${playerName}`,
        email: `token-user-${playerName.toLowerCase().replace(/\s+/g, '-')}@report-card-access.local`
      }, {
        accessType: 'report-card-token',
        playerCode: code,
        playerAssessmentId,
      });
    }
  }, [token, player, loading, error, code, playerAssessmentId]);

  const { data: categoriesResult, loading: loadingCategories, error: categoriesError } = useQuery(GET_REPORT_CATEGORIES, {
    skip: !playerAssessment,
    variables: {
      ageGroupId: playerAssessment?.ageGroup._id,
      positionId: playerAssessment?.position._id
    },
    fetchPolicy: 'cache-and-network'
  });

  const categories = (categoriesResult && categoriesResult.reportWeightCategories) || [];

  // Consolidated error handling using useMemo
  const errorInfo = useMemo(() => {
    if (!playerAssessment && !loading) {
      return {
        title: 'Report Card Not Found',
        body1: 'The report card you\'re looking for could not be found.',
        body2: 'Please check the link or contact the organization for assistance.'
      };
    }

    const mainError = error || categoriesError;
    if (!mainError) return null;

    const errorMessage = mainError.message || '';

    if (token) {
      // Token-based access errors
      if (errorMessage.includes('Invalid or expired access token')) {
        return {
          title: 'Access Link Expired',
          body1: 'The report card access link you\'re using has expired or is no longer valid.',
          body2: 'Please contact the organization to request a new report card link.'
        };
      }

      if (errorMessage.includes('Authentication required')) {
        return {
          title: 'Access Denied',
          body1: 'You need a valid access token to view this report card.',
          body2: 'Please use the link provided by the organization or contact them for assistance.'
        };
      }

      if (errorMessage.includes('Insufficient permissions') || errorMessage.includes('Forbidden')) {
        return {
          title: 'Access Denied',
          body1: 'Your access token doesn\'t have permission to view this report card.',
          body2: 'This may happen if the token was generated for a different player or has insufficient permissions. Please contact the organization for assistance.'
        };
      }

      if (errorMessage.includes('Assessment ID required') || errorMessage.includes('Assessment not found')) {
        return {
          title: 'Report Card Unavailable',
          body1: 'The assessment for this report card could not be found.',
          body2: 'Please contact the organization to verify the report card link is correct.'
        };
      }

      // Generic error for token-based access
      return {
        title: 'Unable to Load Report Card',
        body1: 'There was an error loading the report card.',
        body2: 'Please contact the organization for assistance.'
      };
    } else {
      // Authenticated user errors
      return {
        title: 'Error Loading Report Card',
        body1: errorMessage || 'An unexpected error occurred.'
      };
    }
  }, [error, categoriesError, token, playerAssessment, loading]);

  if (redirecting) return <Typography>Loading...</Typography>;

  if (rejectedLink) {
    return (
      <Box p={4}>
        <AccessLinkRejected reason={rejectedLink} signInPath={signInPath} />
      </Box>
    );
  }

  // Before errorInfo, whose "not found" fallback would otherwise claim any refused report card doesn't exist.
  if (readReportCardUnavailable(error)) return <ReportCardUnavailable />;

  // Single error rendering section
  if (errorInfo) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h5" gutterBottom color="error">
          {errorInfo.title}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {errorInfo.body1}
        </Typography>
        {errorInfo.body2 && (
          <Typography variant="body2" color="textSecondary">
            {errorInfo.body2}
          </Typography>
        )}
      </Box>
    );
  }

  if (!playerAssessment) return <Typography>Loading...</Typography>
  return <ReportCardContainer
    player={player}
    playerAssessment={playerAssessment}
    assessmentReportCards={assessmentReportCards}
    code={code}
    loading={loading}
    loadingCategories={loadingCategories}
    categories={categories}
    token={token}
  />;
};
